import { format, isToday, isThisWeek, isSameYear, formatDistanceToNowStrict } from "date-fns";
import { toZonedTime, formatInTimeZone } from "date-fns-tz";
import { Duration } from "luxon";

interface PrettyDateResult {
  dateTime: string; // Fri 15 Aug 1:39pm 45:002
  dateTimeNoSecs: string; // Fri 15 Aug 1:39pm
  date: string; // Fri 15 Aug
  time: string; // 1:39pm
  timeSecs: string;
  dateTimeSmart: string; // Today → "5:00pm", This week → "Mon 5:00pm", Else → "Mon 29 Sep 5:05pm"
  dist: string; // "2 days ago" | "in 3 hours"
  dateObj: Date;
}

interface PrettyDateOpts {
  now?: Date;
  timezone?: string;
}

export function prettyDateTime(date: any, tz?: string) {
  if (typeof date === "string") {
    date = new Date(date);
  }
  if (!(date instanceof Date)) return undefined;

  return prettyDate(date, { timezone: tz })?.dateTimeNoSecs;
}

export function formatDuration(seconds: number): string {
  // Format as h:mm:ss, don't show leading zeroes where possible,
  // unless it's seconds only, then always show leading 0:0X (e.g. "0:04")
  const dur = Duration.fromObject({ seconds }).shiftTo("hours", "minutes", "seconds");
  const h = dur.hours;
  const m = dur.minutes;
  const s = Math.floor(dur.seconds);

  if (h > 0) {
    // Show h:mm:ss (no leading zero for h, but pad m and s)
    return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  } else if (m > 0) {
    // Show m:ss (no leading zero for m, but pad s)
    return `${m}:${s.toString().padStart(2, "0")}`;
  } else {
    // Show 0:0X for seconds only (e.g. "0:04")
    return `0:${s.toString().padStart(2, "0")}`;
  }
}

/**
 * Formats a date into multiple human-friendly strings
 * @param date - The date to format (Date | string | undefined)
 * @param now - The current date for comparison (defaults to new Date())
 * @returns Object of formatted strings or null if invalid
 */
export function prettyDate(
  date: Date | string | undefined | null,
  opts?: PrettyDateOpts,
): PrettyDateResult | null {
  const { now = new Date(), timezone } = opts ?? {};
  try {
    if (!date) return null;

    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return null;

    // Convert to timezone if provided
    const zonedDate = timezone ? toZonedTime(dateObj, timezone) : dateObj;
    const zonedNow = timezone ? toZonedTime(now, timezone) : now;

    const needsYear = !isSameYear(zonedDate, zonedNow);

    // Base format pieces
    const baseDateFmt = needsYear ? "EEE d MMM yyyy" : "EEE d MMM";

    // Helper to format with timezone awareness
    const formatDate = (date: Date, fmt: string) =>
      timezone ? formatInTimeZone(date, timezone, fmt) : format(date, fmt);

    const dateOnly = formatDate(dateObj, baseDateFmt);
    const baseDateTime = formatDate(dateObj, `${baseDateFmt} h:mmaaa`);

    // Time only (no date)
    const timeOnly = formatDate(dateObj, "h:mmaaa");
    const timeWithSeconds = formatDate(dateObj, "h:mm:ssaaa");

    // Seconds & milliseconds
    const secs = formatDate(dateObj, "ss");
    const millis = formatDate(dateObj, "SSS");

    const prettyStrDate = dateOnly;
    const prettyStrDateTimeNoSeconds = baseDateTime;
    const prettyStrDateTime = `${prettyStrDateTimeNoSeconds} ${secs}:${millis}s`;
    const prettyStrTime = timeOnly;
    const prettyStrTimeSecs = timeWithSeconds;

    // Smart variant
    let prettyStrDateTimeSmart: string;
    if (isToday(zonedDate)) {
      prettyStrDateTimeSmart = formatDate(dateObj, "h:mmaaa"); // "5:00pm"
    } else if (isThisWeek(zonedDate, { weekStartsOn: 1 })) {
      prettyStrDateTimeSmart = formatDate(dateObj, "EEE h:mmaaa"); // "Mon 5:00pm"
    } else {
      prettyStrDateTimeSmart = formatDate(dateObj, `${baseDateFmt} h:mmaaa`); // "Mon 29 Sep 5:05pm"
    }

    // Distance from now
    const prettyStrDateTimeDist = formatDistanceToNowStrict(zonedDate, {
      addSuffix: true,
      unit: "second",
    });

    return {
      dateTime: prettyStrDateTime,
      dateTimeNoSecs: prettyStrDateTimeNoSeconds,
      date: prettyStrDate,
      time: prettyStrTime,
      timeSecs: prettyStrTimeSecs,
      dateTimeSmart: prettyStrDateTimeSmart,
      dist: prettyStrDateTimeDist,
      dateObj: zonedDate,
    };
  } catch {
    return null;
  }
}

export const formatTimeHMS = (
  seconds: number | undefined,
  options?: { showMilliseconds?: boolean },
) => {
  if (seconds === undefined) {
    return "???";
  }
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const millis = Math.floor((seconds % 1) * 1000);

  const showMilliseconds = options?.showMilliseconds ?? false;

  if (hours > 0) {
    const base = `${hours}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    return showMilliseconds ? `${base}.${millis.toString().padStart(3, "0")}` : base;
  } else if (mins > 0) {
    const base = `${mins}:${secs.toString().padStart(2, "0")}`;
    return showMilliseconds ? `${base}.${millis.toString().padStart(3, "0")}` : base;
  } else {
    const base = `0:${secs.toString().padStart(2, "0")}`;
    return showMilliseconds ? `${base}.${millis.toString().padStart(3, "0")}` : base;
  }
};
